/**
 * EmbryoDetailSheet
 *
 * One embryo from a testing report. Overview summarizes its key facts and
 * a linked specimen status. Genetics leads with testing provenance, then
 * PGT-A (including chromosome copies), clinical findings and comparisons.
 * History combines development observations with lifecycle events. It never
 * ranks the embryo; the transfer note is the lab's.
 *
 * Built on the fact-sheet pieces the other detail sheets use: the shared
 * DetailSheet shell, FactSheetHeader, DetailSectionTitle and InfoButton with
 * InfoSheet for explanations.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Platform, ScrollView, View } from "react-native";
import Svg, { G, Line, Rect, Text as SvgText } from "react-native-svg";
import {
  Activity,
  ArrowRight,
  CalendarDays,
  Dna,
  Egg,
  FileCheck2,
  FileText,
  Gauge,
  History,
  Info,
  Microscope,
  NotebookText,
  Shield,
  Snowflake,
  Sparkles,
  Sun,
  type LucideIcon,
} from "lucide-react-native";
import {
  ActionBanner,
  CardSurface,
  CardSubtitleText,
  CardTitleText,
  CompactActionButton,
  DetailSheet,
  DocumentRow,
  MetaText,
  MoonFilterChip,
  MoonPressable,
  MoonSegmentedControl,
  Row,
  StatusBadge,
  type StatusBadgeTone,
} from "@louise/moon-ui";
import {
  colors,
  semanticColors,
  typography,
  withAlpha,
} from "@louise/theme/tokens";
import {
  useTranslations,
  type Locale,
  type TranslationFunction,
} from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import type {
  EmbryoGenomicResult,
  EmbryoReport,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import {
  formatEmbryoGrade,
  readEmbryoStage,
} from "@src/packages/care/cryovault/embryoGrade";
import type { Specimen } from "@src/packages/care/cryovault/useSpecimens";
import {
  chromosomeCopyTone,
  copyNumberScaleMaximum,
  expectedChromosomeCopies,
} from "@src/packages/care/cryovault/embryo-detail/embryoCopyNumbers";
import {
  EMBRYO_RESULT_LABEL_KEYS,
  EMBRYO_RESULT_TONES,
  describeEmbryoResultReason,
  describeEmbryoSex,
  embryoReportSexChromosomes,
  formatEmbryoDate,
  getMosaicRange,
  getEmbryoResultReason,
  getEmbryoStorageState,
  hasPgtAResult,
  hideSexChromosomes,
  isSexChromosome,
  useEmbryoSexDisclosure,
  type EmbryoHistoryEvent,
  type EmbryoResult,
  type EmbryoSexDisclosure,
  type EmbryoStorageState,
  type EmbryoTransferRecommendation,
} from "@src/packages/care/cryovault/embryo-detail";
import {
  getEmbryoTimeline,
  isFertilizationObservation,
  type EmbryoTimelineEvent,
} from "@src/packages/care/cryovault/embryo-detail/embryoTimeline";
import { EMBER } from "../../onboarding/theme";
import DetailSectionTitle from "./DetailSectionTitle";
import FactSheetHeader from "./FactSheetHeader";
import InfoSheet from "./InfoSheet";
import ConditionIllustration from "./ConditionIllustration";
import { SpecimenFactCard, SpecimenFactTile } from "./SpecimenFactCard";
import ShowEmbryoSexButton from "./ShowEmbryoSexButton";
import EmbryoPolygenicComparison, {
  type EmbryoChoice,
} from "./EmbryoPolygenicComparison";

export type { EmbryoChoice } from "./EmbryoPolygenicComparison";

type DevelopmentObservation =
  EmbryoGenomicResult["developmentObservations"][number];
type MonogenicFinding = EmbryoGenomicResult["monogenicFindings"][number];
type PolygenicRisk = EmbryoGenomicResult["polygenicRisks"][number];
type ChromosomeCopyNumber = NonNullable<
  EmbryoGenomicResult["cnProfile"]
>[number];

export type EmbryoDetailSheetProps = {
  embryo: EmbryoGenomicResult | null;
  report: EmbryoReport;
  /** The embryo's cryo vault record, when the report links one. */
  specimen: Specimen | null;
  fertilizationTechnique: "ivf" | "icsi" | null;
  /** "Transferred September 10, 2026, pregnancy confirmed", when it was. */
  transferOutcome: string | null;
  onClose: () => void;
  onOpenLabReport?: () => void;
  onOpenInCryoVault?: () => void;
  onPlanTransfer?: () => void;
  onAskLouise?: (prompt: string) => void;
  /** The page's other embryos, so the patient can move between them here. */
  embryoChoices?: EmbryoChoice[];
  onSelectEmbryo?: (embryoId: string) => void;
};

/** Result colours for chart marks, matching the cryo vault's PGT colours. */
const EMBRYO_RESULT_COLORS: Record<EmbryoResult, string> = {
  euploid: EMBER.colors.green,
  mosaic: EMBER.colors.gold,
  aneuploid: EMBER.colors.coral,
  no_result: EMBER.colors.textLight,
};

const RESULT_MEANING_KEYS: Record<EmbryoResult, string> = {
  euploid: "care.embryo_detail.result_meaning.expected_chromosome_count_found",
  mosaic: "care.embryo_detail.result_meaning.some_cells_have_different_copies",
  aneuploid: "care.embryo_detail.result_meaning.extra_or_missing_chromosome",
  no_result: "care.embryo_detail.result_meaning.lab_could_not_get_result",
};

const RECOMMENDATION_KEYS: Record<EmbryoTransferRecommendation, string> = {
  yes: "care.embryo_detail.lab_recommendation.recommended_for_transfer",
  with_counseling:
    "care.embryo_detail.lab_recommendation.counseling_before_transfer",
  no: "care.embryo_detail.lab_recommendation.not_recommended_for_transfer",
};

const RECOMMENDATION_BANNERS = {
  yes: "encouragement",
  with_counseling: "warning",
  no: "important",
} as const;

const STORAGE_STATUS_KEYS: Record<EmbryoStorageState["kind"], string> = {
  frozen: "care.embryo_report.storage_status.frozen",
  transferred: "care.embryo_report.storage_status.transferred",
  thawed: "care.embryo_report.storage_status.thawed",
  discarded: "care.embryo_report.storage_status.discarded",
  donated: "care.embryo_report.storage_status.donated",
  in_transit: "care.embryo_report.storage_status.moving_between_clinics",
};

const STAGE_KEYS: Record<
  NonNullable<DevelopmentObservation["stage"]>,
  string
> = {
  early_compacted: "care.embryo_detail.stage.early_compacted",
  compacted: "care.embryo_detail.stage.compacted",
  early_blastocyst: "care.embryo_detail.stage.early_blastocyst",
  blastocyst: "care.embryo_detail.stage.blastocyst",
  full_blastocyst: "care.embryo_detail.stage.full_blastocyst",
  expanded_blastocyst: "care.embryo_detail.stage.expanded_blastocyst",
  hatching_blastocyst: "care.embryo_detail.stage.hatching_blastocyst",
  fully_hatched_blastocyst: "care.embryo_detail.stage.fully_hatched_blastocyst",
};

const FINDING_STATUS: Record<
  MonogenicFinding["status"],
  { key: string; tone: StatusBadgeTone }
> = {
  unaffected: {
    key: "care.embryo_detail.finding_status.unaffected",
    tone: "green",
  },
  carrier: {
    key: "care.embryo_detail.finding_status.carrier",
    tone: "neutral",
  },
  affected: {
    key: "care.embryo_detail.finding_status.affected",
    tone: "coral",
  },
};

type EmbryoTerm = EmbryoResult | "grade" | "biopsy" | "karyotype";

type EmbryoTab = "overview" | "genetics" | "history";

const TERM_KEYS: Record<EmbryoTerm, { title: string; body: string }> = {
  euploid: {
    title: "care.embryo_report.result_euploid",
    body: "care.embryo_detail.term.what_euploid_means",
  },
  mosaic: {
    title: "care.embryo_report.result_mosaic",
    body: "care.embryo_detail.term.what_mosaic_means",
  },
  aneuploid: {
    title: "care.embryo_report.result_aneuploid",
    body: "care.embryo_detail.term.what_aneuploid_means",
  },
  no_result: {
    title: "care.embryo_report.result_no_result",
    body: "care.embryo_detail.term.what_no_result_means",
  },
  grade: {
    title: "care.embryo_detail.term.embryo_grade",
    body: "care.embryo_detail.term.how_grades_read",
  },
  biopsy: {
    title: "care.embryo_detail.term.biopsy_day",
    body: "care.embryo_detail.term.what_the_biopsy_is",
  },
  karyotype: {
    title: "care.embryo_detail.term.karyotype",
    body: "care.embryo_detail.term.how_karyotypes_read",
  },
};

const HISTORY_KEYS: Record<EmbryoHistoryEvent["kind"], string> = {
  retrieved: "care.embryo_detail.history.egg_retrieved",
  created: "care.embryo_detail.history.embryo_created",
  biopsied: "care.embryo_detail.history.biopsied",
  frozen: "care.embryo_detail.history.frozen",
  reported: "care.embryo_detail.history.result_reported",
  thawed: "care.embryo_detail.history.thawed",
  transferred: "care.embryo_detail.history.transferred",
};

const HISTORY_ICONS: Record<EmbryoTimelineEvent["kind"], LucideIcon> = {
  retrieved: Egg,
  created: Dna,
  biopsied: Microscope,
  frozen: Snowflake,
  reported: FileCheck2,
  thawed: Sun,
  transferred: ArrowRight,
  development: Microscope,
};

/** A titled block, spaced like the other fact sheets' sections. */
function Section({
  title,
  icon,
  children,
  testID,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  testID?: string;
}) {
  return (
    <View testID={testID} style={{ marginTop: 24 }}>
      <DetailSectionTitle icon={icon}>{title}</DetailSectionTitle>
      <View style={{ gap: 12 }}>{children}</View>
    </View>
  );
}

// ── Chromosomes ──

/**
 * Copies of each chromosome against what is typical for it. Autosomes are
 * typical at two copies; X and Y are judged against the embryo's sex, so a
 * male embryo's single X sits on its own line instead of reading as a loss.
 */
function CopyNumberChart({
  profile,
  sex,
  mosaicChromosomes,
  t,
  locale,
}: {
  profile: ChromosomeCopyNumber[];
  sex: EmbryoGenomicResult["sex"];
  mosaicChromosomes: ReadonlySet<string>;
  t: TranslationFunction;
  locale: Locale;
}) {
  const points = profile.filter(
    (point) => Number.isFinite(point.cn) && point.cn >= 0,
  );
  const groups = Array.from(
    { length: Math.ceil(points.length / 8) },
    (_, index) => points.slice(index * 8, index * 8 + 8),
  );
  const formatCopies = (copies: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(copies);
  // The chart keeps its own palette; the result tags take the status roles.
  const colors = {
    expected: "#B6B9BB",
    mosaic: semanticColors.chart.chromosome.mosaic,
    different: semanticColors.chart.chromosome.different,
  };
  const reportedTones = new Set(
    points.map((point) => chromosomeCopyTone(point, mosaicChromosomes)),
  );
  const width = 320;
  const height = 130;
  const left = 24;
  const right = 4;
  const top = 20;
  const bottom = 26;
  const plotHeight = height - top - bottom;
  const maxCopies = copyNumberScaleMaximum(points);
  const grid = [...new Set([0, 2, maxCopies])];
  const yFor = (copies: number) =>
    top + plotHeight - (copies / maxCopies) * plotHeight;
  // All groups keep the same spacing, including a short final group.
  const step = (width - left - right) / 8;

  return (
    <View style={{ gap: 12 }}>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
        }}
      >
        {(["expected", "mosaic", "different"] as const)
          .filter((tone) => reportedTones.has(tone))
          .map((tone) => (
            <View
              key={tone}
              style={{ flexDirection: "row", alignItems: "center", gap: 5 }}
            >
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: colors[tone],
                }}
              />
              <MetaText>
                {t(
                  tone === "expected"
                    ? "care.embryo_detail.expected_copy_number"
                    : tone === "mosaic"
                      ? "care.embryo_detail.mosaic_reported"
                      : "care.embryo_detail.copy_number_change_reported",
                )}
              </MetaText>
            </View>
          ))}
      </View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <Svg width={24} height={10}>
          <Line
            x1={0}
            y1={5}
            x2={24}
            y2={5}
            stroke="#73777A"
            strokeWidth={1.5}
            strokeDasharray="4,3"
          />
        </Svg>
        <MetaText>{t("care.embryo_detail.expected_number_of_copies")}</MetaText>
      </View>
      {groups.map((group, groupIndex) => (
        <CardSurface key={groupIndex} variant="panel" style={{ padding: 6 }}>
          <Svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
            <SvgText
              x={0}
              y={10}
              fill={EMBER.colors.textMid}
              fontFamily={typography.families.sans}
              fontSize={10}
              textAnchor="start"
            >
              {t("care.embryo_detail.number_of_copies")}
            </SvgText>
            {grid.map((copies) => (
              <G key={copies}>
                <Line
                  x1={left}
                  y1={yFor(copies)}
                  x2={width - right}
                  y2={yFor(copies)}
                  stroke={EMBER.colors.border}
                  strokeWidth={0.75}
                />
                <SvgText
                  x={left - 7}
                  y={yFor(copies) + 3.5}
                  fill={EMBER.colors.textMid}
                  fontFamily={typography.families.sans}
                  fontSize={10}
                  textAnchor="end"
                >
                  {formatCopies(copies)}
                </SvgText>
              </G>
            ))}
            {group.map((point, index) => {
              const x = left + index * step + step / 2;
              const expected = expectedChromosomeCopies(point.chr, sex);
              const tone = chromosomeCopyTone(point, mosaicChromosomes);
              const color = colors[tone];
              const barY = yFor(point.cn);
              return (
                <G key={point.chr}>
                  <Rect
                    x={x - 9}
                    y={barY}
                    width={18}
                    height={yFor(0) - barY}
                    rx={3}
                    fill={color}
                    fillOpacity={0.3}
                    stroke={color}
                    strokeOpacity={0.7}
                    strokeWidth={0.75}
                  />
                  <Line
                    x1={x - 14}
                    y1={yFor(expected)}
                    x2={x + 14}
                    y2={yFor(expected)}
                    stroke="#73777A"
                    strokeWidth={1.25}
                    strokeDasharray="4,3"
                  />
                  <SvgText
                    x={x}
                    y={barY - 7}
                    fill={tone === "expected" ? EMBER.colors.textMid : color}
                    fontFamily={typography.families.sansBold}
                    fontSize={11}
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    {formatCopies(point.cn)}
                  </SvgText>
                  <SvgText
                    x={x}
                    y={height - 7}
                    fill={tone === "expected" ? EMBER.colors.text : color}
                    fontFamily={typography.families.sansBold}
                    fontSize={12}
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    {point.chr}
                  </SvgText>
                </G>
              );
            })}
          </Svg>
        </CardSurface>
      ))}
    </View>
  );
}

/** Read the full reported profile and expected counts without relying on color. */
function describeCopyNumbers(
  profile: ChromosomeCopyNumber[],
  sex: EmbryoGenomicResult["sex"],
  t: TranslationFunction,
): string {
  return t("care.embryo_detail.reported_chromosome_copy_numbers", {
    params: {
      values: profile
        .map((point) =>
          t(
            point.cn === 1
              ? "care.embryo_detail.single_chromosome_copy_found_vs_expected"
              : "care.embryo_detail.chromosome_copies_found_vs_expected",
            {
              params: {
                chromosome: point.chr,
                copies: String(point.cn),
                expected: String(expectedChromosomeCopies(point.chr, sex)),
              },
            },
          ),
        )
        .join("; "),
    },
  });
}

function describeAffectedChromosome(
  embryo: EmbryoGenomicResult,
  chromosome: string,
  t: TranslationFunction,
): string {
  const point = embryo.cnProfile?.find(
    (candidate) => candidate.chr === chromosome,
  );
  const expected = expectedChromosomeCopies(chromosome, embryo.sex);
  if (point && point.cn > expected) {
    return t("care.embryo_report.result_reason.extra_copy_of_chromosome", {
      params: { chromosome },
    });
  }
  if (point && point.cn < expected) {
    return t("care.embryo_report.result_reason.missing_copy_of_chromosome", {
      params: { chromosome },
    });
  }
  return t("care.embryo_report.result_reason.changes_on_chromosomes", {
    params: { chromosomes: chromosome },
  });
}

function formatMosaicPercentage(level: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 20,
  }).format(level);
}

function ChromosomeResultSection({
  embryo,
  report,
  sexDisclosure,
  onExplainKaryotype,
  onExplainResult,
  t,
  locale,
}: {
  embryo: EmbryoGenomicResult;
  report: EmbryoReport;
  sexDisclosure: EmbryoSexDisclosure;
  onExplainKaryotype: () => void;
  onExplainResult: () => void;
  t: TranslationFunction;
  locale: Locale;
}) {
  const mosaicism = embryo.mosaicism ?? [];
  const affected = embryo.affectedChromosomes ?? [];
  const resultLabel = t(EMBRYO_RESULT_LABEL_KEYS[embryo.result]);
  const title = hasPgtAResult(report.testType)
    ? t("care.embryo_detail.pgta_result")
    : t("care.embryo_detail.chromosome_result");
  // While the sex is hidden, the notation keeps its count and changes and
  // masks only the sex chromosomes, so the result itself still reads.
  const notation = sexDisclosure.hidden
    ? hideSexChromosomes(embryo.karyotype)
    : embryo.karyotype;
  const notationHidesSex = notation !== embryo.karyotype;

  return (
    <Section title={title} icon={Dna} testID="embryo-pgta-result-section">
      <SpecimenFactCard
        testID="embryo-pgta-result-card"
        style={{ marginTop: 0 }}
      >
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <SpecimenFactTile
            style={{ minWidth: 0 }}
            icon={Dna}
            label={t("care.embryo_detail.reported_chromosome_result")}
            value={resultLabel}
            onPress={onExplainResult}
          >
            <View style={{ alignItems: "flex-start", paddingTop: 2 }}>
              <StatusBadge
                label={resultLabel}
                tone={EMBRYO_RESULT_TONES[embryo.result]}
              />
            </View>
          </SpecimenFactTile>
          {embryo.karyotype && (
            <SpecimenFactTile
              style={{ minWidth: 0 }}
              icon={Dna}
              label={t("care.embryo_detail.laboratory_notation")}
              value={notation}
              trailingIcon={Info}
              onTrailingPress={onExplainKaryotype}
              trailingAccessibilityLabel={t(
                "care.embryo_detail.term.what_term_means",
                {
                  params: { term: t("care.embryo_detail.term.karyotype") },
                },
              )}
            >
              {notationHidesSex ? (
                <>
                  <CardTitleText>{notation}</CardTitleText>
                  <ShowEmbryoSexButton onPress={sexDisclosure.reveal} />
                </>
              ) : undefined}
            </SpecimenFactTile>
          )}
          {affected.map((chromosome) => (
            <SpecimenFactTile
              fullWidth
              key={chromosome}
              icon={Dna}
              label={t("care.embryo_detail.chromosome_finding")}
              value={describeAffectedChromosome(embryo, chromosome, t)}
            />
          ))}
          {mosaicism.map((mosaic) => {
            const range = t(
              getMosaicRange(mosaic.level) === "low"
                ? "care.embryo_detail.mosaic_low_range"
                : "care.embryo_detail.mosaic_high_range",
            );
            return (
              <SpecimenFactTile
                fullWidth
                key={mosaic.chromosome}
                icon={Dna}
                label={t("care.embryo_detail.chromosome_finding")}
                value={t("care.embryo_detail.mosaic_finding_with_percentage", {
                  params: {
                    range,
                    chromosome: mosaic.chromosome,
                    percentage: formatMosaicPercentage(mosaic.level, locale),
                  },
                })}
              />
            );
          })}
        </View>
        {(embryo.result !== "no_result" || embryo.aneuploidyImpact) && (
          <CardSubtitleText>
            {embryo.aneuploidyImpact || t(RESULT_MEANING_KEYS[embryo.result])}
          </CardSubtitleText>
        )}
      </SpecimenFactCard>
      <ChromosomeCopies
        embryo={embryo}
        sexHidden={sexDisclosure.hidden}
        onRevealSex={notationHidesSex ? undefined : sexDisclosure.reveal}
        t={t}
        locale={locale}
      />
    </Section>
  );
}

function ChromosomeCopies({
  embryo,
  sexHidden,
  onRevealSex,
  t,
  locale,
}: {
  embryo: EmbryoGenomicResult;
  sexHidden: boolean;
  /** Set when no other control in the section reveals the sex. */
  onRevealSex?: () => void;
  t: TranslationFunction;
  locale: Locale;
}) {
  const reported = embryo.cnProfile ?? [];
  // The X and Y bars give the sex away, so they wait for her to reveal it.
  const profile = sexHidden
    ? reported.filter((point) => !isSexChromosome(point.chr))
    : reported;
  const sexBarsHidden = profile.length < reported.length;
  const mosaicism = embryo.mosaicism ?? [];
  const mosaicChromosomes = new Set(
    mosaicism.map((mosaic) => mosaic.chromosome),
  );
  if (profile.length === 0) return null;

  return (
    <View style={{ gap: 12 }}>
      <CardTitleText>
        {t("care.embryo_detail.chromosome_copy_numbers")}
      </CardTitleText>
      <CardSubtitleText>
        {t("care.embryo_detail.read_copy_number_bars")}
      </CardSubtitleText>
      <View
        testID="embryo-chromosome-copies-chart"
        accessible
        accessibilityRole="image"
        accessibilityLabel={describeCopyNumbers(profile, embryo.sex, t)}
      >
        <CopyNumberChart
          profile={profile}
          sex={embryo.sex}
          mosaicChromosomes={mosaicChromosomes}
          t={t}
          locale={locale}
        />
      </View>
      {sexBarsHidden && onRevealSex && (
        <ShowEmbryoSexButton onPress={onRevealSex} />
      )}
    </View>
  );
}

// ── Development ──

function describeObservation(
  observation: DevelopmentObservation,
  t: TranslationFunction,
): string {
  const grade = formatEmbryoGrade(observation);
  const stage = readEmbryoStage(observation);
  const parts = [
    observation.isArrested ? t("care.embryo_detail.stopped_developing") : null,
    stage ? t(STAGE_KEYS[stage]) : null,
    observation.cellCount != null
      ? t("care.embryo_detail.cell_count", {
          params: { count: String(observation.cellCount) },
        })
      : null,
    observation.pronucleiCount,
    observation.polarBodyCount,
    grade
      ? stage
        ? grade
        : t("care.embryo_detail.grade_value", { params: { grade } })
      : null,
  ].filter((part): part is string => Boolean(part));

  return parts.join(", ");
}

// ── History ──

function HistorySection({
  events,
  t,
  locale,
}: {
  events: EmbryoTimelineEvent[];
  t: TranslationFunction;
  locale: Locale;
}) {
  const visibleEvents = events.filter(
    (event) =>
      event.kind !== "development" ||
      describeObservation(event.observation, t).length > 0 ||
      Boolean(event.observation.notes?.trim()),
  );
  if (visibleEvents.length === 0) return null;
  const hasUnconfirmed = visibleEvents.some(
    (event) =>
      event.kind === "development" &&
      event.observation.verificationStatus !== "verified",
  );
  return (
    <View style={{ marginTop: 24, gap: 12 }}>
      <View
        testID="embryo-history-timeline"
        style={{ gap: 0, paddingHorizontal: 4 }}
      >
        {visibleEvents.map((event, index) => {
          const title =
            event.kind === "development"
              ? t(
                  isFertilizationObservation(event.observation)
                    ? "care.embryo_detail.history.fertilization_check"
                    : "care.embryo_detail.history.development_check",
                )
              : t(HISTORY_KEYS[event.kind]);
          const Icon = HISTORY_ICONS[event.kind];
          const date =
            event.kind === "development"
              ? t("care.embryo_detail.development_day", {
                  params: { day: String(event.observation.day) },
                })
              : formatDate(event.date.slice(0, 10), locale, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
          const outcome =
            event.kind === "development"
              ? describeObservation(event.observation, t)
              : null;
          return (
            <View
              key={
                event.kind === "development"
                  ? event.observation.id
                  : `${event.kind}-${index}`
              }
              testID={`embryo-history-step-${index}`}
              style={{ flexDirection: "row", gap: 10 }}
            >
              <View style={{ alignItems: "center", width: 18 }}>
                <View testID="embryo-history-icon" style={{ marginTop: 3 }}>
                  <Icon
                    size={16}
                    strokeWidth={1.5}
                    color={EMBER.colors.textLight}
                  />
                </View>
                {index < visibleEvents.length - 1 ? (
                  <View
                    style={{
                      width: 1,
                      flex: 1,
                      minHeight: 28,
                      borderLeftWidth: 1,
                      borderStyle: "dashed",
                      borderColor: withAlpha(colors.greyBrown, 0.28),
                      marginTop: 4,
                      marginBottom: 4,
                    }}
                  />
                ) : null}
              </View>
              <View
                style={{
                  flex: 1,
                  gap: 2,
                  paddingBottom: index < visibleEvents.length - 1 ? 16 : 0,
                }}
              >
                <View testID="embryo-history-title">
                  <CardTitleText>{title}</CardTitleText>
                </View>
                <View testID="embryo-history-date">
                  <MetaText>{date}</MetaText>
                </View>
                {event.kind === "development" && (
                  <View testID="embryo-history-development" style={{ gap: 4 }}>
                    {outcome && (
                      <View testID="embryo-history-outcome">
                        <CardSubtitleText>{outcome}</CardSubtitleText>
                      </View>
                    )}
                    {event.observation.notes?.trim() && (
                      <View testID="embryo-history-note">
                        <CardSubtitleText>
                          {event.observation.notes}
                        </CardSubtitleText>
                      </View>
                    )}
                  </View>
                )}
                {event.kind === "reported" && (
                  <View
                    testID="embryo-history-outcome"
                    style={{ alignItems: "flex-start", marginTop: 2 }}
                  >
                    <StatusBadge
                      label={t(EMBRYO_RESULT_LABEL_KEYS[event.result])}
                      tone={EMBRYO_RESULT_TONES[event.result]}
                    />
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </View>
      {hasUnconfirmed && (
        <MetaText>
          {t("care.embryo_detail.some_entries_not_confirmed")}
        </MetaText>
      )}
    </View>
  );
}

// ── Whole-genome findings ──

function MonogenicSection({
  findings,
  provider,
  t,
}: {
  findings: MonogenicFinding[];
  provider: string | null;
  t: TranslationFunction;
}) {
  return (
    <Section
      title={t("care.embryo_detail.single_gene_conditions")}
      icon={Microscope}
    >
      {provider && (
        <MetaText>
          {t("care.embryo_detail.tested_by_provider", { params: { provider } })}
        </MetaText>
      )}
      <View style={{ gap: 8 }}>
        {findings.map((finding) => (
          <Row
            key={finding.id}
            testID={`embryo-monogenic-row-${finding.id}`}
            elevated={false}
            leading={<ConditionIllustration condition={finding.condition} />}
            title={finding.condition}
            titleRight={
              <StatusBadge
                label={t(FINDING_STATUS[finding.status].key)}
                tone={FINDING_STATUS[finding.status].tone}
              />
            }
            subtitle={[
              `${finding.gene} · ${finding.inheritancePattern}`,
              finding.clinicalSignificance,
            ]
              .filter((line): line is string => Boolean(line))
              .join("\n")}
          />
        ))}
      </View>
    </Section>
  );
}

function PolygenicRiskRow({
  risk,
  t,
}: {
  risk: PolygenicRisk;
  t: TranslationFunction;
}) {
  return (
    <Row
      testID={`embryo-polygenic-risk-row-${risk.id}`}
      elevated={false}
      leading={<ConditionIllustration condition={risk.condition} />}
      title={risk.condition}
      subtitle={t("care.embryo_detail.population_average_risk", {
        params: { risk: risk.populationRisk },
      })}
      titleRight={
        risk.isElevated ? (
          <StatusBadge
            label={t("care.embryo_detail.elevated_risk")}
            tone="coral"
          />
        ) : undefined
      }
      trailing={
        <View style={{ alignItems: "flex-end", gap: 2 }}>
          <CardTitleText>{risk.absoluteRisk}</CardTitleText>
          <MetaText>{t("care.embryo_detail.lifetime_risk")}</MetaText>
        </View>
      }
    />
  );
}

/** Absolute risk leads each condition; the sheet offers one condition at a time. */
function PolygenicSection({
  risks,
  t,
}: {
  risks: PolygenicRisk[];
  t: TranslationFunction;
}) {
  const groups = useMemo(() => {
    const byCategory = new Map<string, PolygenicRisk[]>();
    for (const risk of risks) {
      const categoryRisks = byCategory.get(risk.category) ?? [];
      categoryRisks.push(risk);
      byCategory.set(risk.category, categoryRisks);
    }
    return [...byCategory.entries()].sort(([left], [right]) =>
      left.localeCompare(right),
    );
  }, [risks]);

  return (
    <Section
      title={t("care.embryo_detail.polygenic_risk_scores")}
      icon={Activity}
    >
      {groups.map(([category, categoryRisks]) => (
        <View key={category} style={{ gap: 8 }}>
          <MetaText>{category}</MetaText>
          {categoryRisks.map((risk) => (
            <PolygenicRiskRow key={risk.id} risk={risk} t={t} />
          ))}
        </View>
      ))}
    </Section>
  );
}

/** Assay and sample provenance use the same fact cards as Overview. */
function TestingFacts({
  report,
  onOpenLabReport,
  t,
}: {
  report: EmbryoReport;
  onOpenLabReport?: () => void;
  t: TranslationFunction;
}) {
  const testingDetails = [report.testType, report.laboratory]
    .filter(Boolean)
    .join(" · ");
  if (!testingDetails && !report.sampleType && !onOpenLabReport) return null;

  return (
    <SpecimenFactCard testID="embryo-testing-facts" style={{ marginTop: 24 }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {testingDetails && (
          <SpecimenFactTile
            icon={Microscope}
            label={t("care.embryo_detail.testing_performed")}
            value={testingDetails}
          >
            <CardTitleText>
              {report.testType ?? report.laboratory}
            </CardTitleText>
            {report.testType && report.laboratory && (
              <MetaText>{report.laboratory}</MetaText>
            )}
          </SpecimenFactTile>
        )}
        {report.sampleType && (
          <SpecimenFactTile
            icon={FileText}
            label={t("care.embryo_detail.embryo_sample_tested")}
            value={report.sampleType}
          />
        )}
      </View>
      {onOpenLabReport && (
        <DocumentRow
          leading={
            <FileText
              size={18}
              color={EMBER.colors.textLight}
              strokeWidth={1.5}
            />
          }
          title={t("care.embryo_detail.open_lab_report")}
          titleNumberOfLines={2}
          subtitle={report.attachment?.upload?.name ?? undefined}
          onPress={onOpenLabReport}
          action="open_embryo_lab_report"
        />
      )}
    </SpecimenFactCard>
  );
}

// ── Sheet ──

function EmbryoDetailContent({
  embryo,
  report,
  specimen,
  fertilizationTechnique,
  transferOutcome,
  onOpenLabReport,
  onOpenInCryoVault,
  onPlanTransfer,
  onAskLouise,
  tab,
  onChangeTab,
  onExplainTerm,
}: Omit<EmbryoDetailSheetProps, "embryo" | "onClose" | "onSelectEmbryo"> & {
  embryo: EmbryoGenomicResult;
  tab: EmbryoTab;
  onChangeTab: (tab: EmbryoTab) => void;
  onExplainTerm: (term: EmbryoTerm) => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const sexDisclosure = useEmbryoSexDisclosure(embryo.id);
  const sexChromosomes = embryoReportSexChromosomes(embryo);
  const sex = sexChromosomes ? describeEmbryoSex(sexChromosomes, t) : null;

  const storage = specimen ? getEmbryoStorageState(specimen) : null;
  const history = getEmbryoTimeline(report, embryo, specimen);
  const reason = getEmbryoResultReason(embryo);
  const reasonText = reason ? describeEmbryoResultReason(reason, t) : null;
  const name = t("care.embryo_report.embryo_number", {
    params: { number: String(embryo.embryoNumber) },
  });
  const resultLabel = t(EMBRYO_RESULT_LABEL_KEYS[embryo.result]);
  const explainLabel = (label: string) =>
    t("care.embryo_detail.term.what_term_means", { params: { term: label } });

  const latestObservation = [...embryo.developmentObservations].sort(
    (a, b) => b.day - a.day,
  )[0];
  const observedGrade = latestObservation
    ? formatEmbryoGrade(latestObservation)
    : null;
  const grade = embryo.grade ?? observedGrade;
  const biopsyDate = formatEmbryoDate(embryo.biopsyDate, locale);
  const biopsyDay =
    embryo.biopsyDay != null
      ? t("care.embryo_detail.biopsied_on_day", {
          params: { day: String(embryo.biopsyDay) },
        })
      : null;
  const eggSource =
    specimen?.isDonor === true
      ? t("care.embryo_detail.donor_eggs")
      : specimen?.isDonor === false
        ? t("care.embryo_detail.your_eggs")
        : null;
  const technique =
    fertilizationTechnique === "icsi"
      ? t("care.embryo_detail.technique_icsi")
      : fertilizationTechnique === "ivf"
        ? t("care.embryo_detail.technique_ivf")
        : null;
  const createdFrom = [eggSource, technique].filter(Boolean).join(", ");

  const askLouisePrompt = t("care.embryo_detail.ask_louise_prompt", {
    params: {
      embryo: name,
      details: [
        resultLabel,
        reasonText,
        grade
          ? t("care.embryo_detail.grade_value", { params: { grade } })
          : null,
      ]
        .filter(Boolean)
        .join(", "),
    },
  });

  return (
    <>
      <FactSheetHeader
        title={name}
        subtitle={t("care.embryo_detail.lab_id", {
          params: { id: embryo.sampleId },
        })}
        visual={<OrganicMiniIcon kind="genetics" size={92} />}
        badges={
          embryo.result === "euploid"
            ? []
            : [
                {
                  label: resultLabel,
                  color: EMBRYO_RESULT_COLORS[embryo.result],
                  accessibilityLabel: explainLabel(resultLabel),
                  onPress: () => onExplainTerm(embryo.result),
                },
              ]
        }
      />
      {embryo.result === "euploid" && (
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            marginTop: 4,
            minHeight: 26,
          }}
        >
          <MoonPressable
            testID="embryo-header-result-tag"
            accessibilityRole="button"
            accessibilityLabel={explainLabel(resultLabel)}
            action="fact_sheet_nav"
            hitSlop={12}
            onPress={() => onExplainTerm(embryo.result)}
          >
            <StatusBadge
              label={resultLabel}
              tone={EMBRYO_RESULT_TONES[embryo.result]}
            />
          </MoonPressable>
        </View>
      )}
      {reasonText && (
        <CardSubtitleText style={{ textAlign: "center", marginTop: 8 }}>
          {reasonText}
        </CardSubtitleText>
      )}

      <MoonSegmentedControl
        active={tab}
        onChange={onChangeTab}
        tabs={[
          {
            value: "overview",
            label: t("care.embryo_detail.tabs.overview"),
            icon: Info,
          },
          {
            value: "genetics",
            label: t("care.embryo_detail.tabs.genetics"),
            icon: Dna,
          },
          {
            value: "history",
            label: t("care.embryo_detail.tabs.history"),
            icon: History,
          },
        ]}
      />

      {tab === "overview" && (
        <>
          {embryo.transferRecommendation !== "yes" && (
            <View style={{ marginTop: 20 }}>
              <ActionBanner
                variant={RECOMMENDATION_BANNERS[embryo.transferRecommendation]}
                message={t(RECOMMENDATION_KEYS[embryo.transferRecommendation])}
              />
            </View>
          )}

          <Section title={t("care.embryo_detail.at_a_glance")} icon={Info}>
            <SpecimenFactCard
              testID="embryo-overview-facts"
              style={{ marginTop: 0 }}
            >
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {grade && (
                  <SpecimenFactTile
                    style={{ minWidth: 0 }}
                    icon={Gauge}
                    label={t("care.embryo_detail.grade")}
                    value={grade}
                    onPress={() => onExplainTerm("grade")}
                  >
                    <CardTitleText>{grade}</CardTitleText>
                    {latestObservation && observedGrade === grade && (
                      <MetaText>
                        {t("care.embryo_detail.development_day", {
                          params: { day: String(latestObservation.day) },
                        })}
                      </MetaText>
                    )}
                  </SpecimenFactTile>
                )}
                {(biopsyDay || biopsyDate) && (
                  <SpecimenFactTile
                    style={{ minWidth: 0 }}
                    icon={Microscope}
                    label={t("care.embryo_detail.biopsy")}
                    value={[biopsyDay, biopsyDate].filter(Boolean).join(", ")}
                    onPress={() => onExplainTerm("biopsy")}
                  >
                    <CardTitleText>{biopsyDay ?? biopsyDate}</CardTitleText>
                    {biopsyDay && biopsyDate && (
                      <MetaText>{biopsyDate}</MetaText>
                    )}
                  </SpecimenFactTile>
                )}
                {createdFrom && (
                  <SpecimenFactTile
                    style={{ minWidth: 0 }}
                    icon={Egg}
                    label={t("care.embryo_detail.created_from")}
                    value={createdFrom}
                  />
                )}
                {sex && (
                  <SpecimenFactTile
                    style={{ minWidth: 0 }}
                    icon={Dna}
                    label={
                      sexDisclosure.hidden
                        ? t("care.embryo_sex.sex")
                        : sex.label
                    }
                    value={sexDisclosure.hidden ? undefined : sex.value}
                  >
                    {sexDisclosure.hidden ? (
                      <ShowEmbryoSexButton onPress={sexDisclosure.reveal} />
                    ) : (
                      <CardTitleText>{sex.value}</CardTitleText>
                    )}
                  </SpecimenFactTile>
                )}
                {storage && (
                  <SpecimenFactTile
                    fullWidth
                    icon={Shield}
                    label={t("care.embryo_detail.storage_status")}
                    value={t(STORAGE_STATUS_KEYS[storage.kind])}
                    onPress={onOpenInCryoVault}
                    pressIndicator="chevron"
                  >
                    {storage.kind === "transferred" ? (
                      <View style={{ alignItems: "flex-start", gap: 3 }}>
                        <StatusBadge
                          label={t(STORAGE_STATUS_KEYS[storage.kind])}
                          tone="neutral"
                        />
                        {transferOutcome && (
                          <MetaText>{transferOutcome}</MetaText>
                        )}
                      </View>
                    ) : undefined}
                  </SpecimenFactTile>
                )}
              </View>
              {!storage && onOpenInCryoVault && (
                <DocumentRow
                  leading={<OrganicMiniIcon kind="cryo" size={34} />}
                  title={t("care.embryo_detail.view_in_cryo_vault")}
                  onPress={onOpenInCryoVault}
                  elevated={false}
                  action="open_embryo_in_cryo_vault"
                />
              )}
            </SpecimenFactCard>
          </Section>

          {(onPlanTransfer || onAskLouise) && (
            <View
              style={{
                flexDirection: "row",
                gap: 8,
                marginTop: 28,
              }}
            >
              {onPlanTransfer && (
                <CompactActionButton
                  label={t("care.embryo_detail.plan_transfer_with_embryo")}
                  leadingIcon={CalendarDays}
                  size="sm"
                  variant="primary"
                  onPress={onPlanTransfer}
                  action="plan_transfer_with_embryo"
                  flexGrow={1}
                  minWidth={0}
                  style={{ flexShrink: 1 }}
                  textStyle={{ flexShrink: 1 }}
                />
              )}
              {onAskLouise && (
                <CompactActionButton
                  label={t("care.embryo_detail.ask_louise_about_embryo")}
                  leadingIcon={Sparkles}
                  size="sm"
                  variant="secondary"
                  onPress={() => onAskLouise(askLouisePrompt)}
                  action="ask_louise_about_embryo"
                  flexGrow={1}
                  minWidth={0}
                  style={{ flexShrink: 1 }}
                  textStyle={{ flexShrink: 1 }}
                />
              )}
            </View>
          )}
        </>
      )}

      {tab === "genetics" && (
        <>
          <TestingFacts
            report={report}
            onOpenLabReport={onOpenLabReport}
            t={t}
          />
          <ChromosomeResultSection
            embryo={embryo}
            report={report}
            sexDisclosure={sexDisclosure}
            onExplainKaryotype={() => onExplainTerm("karyotype")}
            onExplainResult={() => onExplainTerm(embryo.result)}
            t={t}
            locale={locale}
          />
          {embryo.monogenicFindings.length > 0 && (
            <MonogenicSection
              findings={embryo.monogenicFindings}
              provider={embryo.genomicsProvider}
              t={t}
            />
          )}
          {embryo.polygenicRisks.length > 0 && (
            <PolygenicSection risks={embryo.polygenicRisks} t={t} />
          )}
        </>
      )}

      {tab === "history" && (
        <>
          <HistorySection events={history} t={t} locale={locale} />
          {embryo.comments && (
            <Section
              title={t("care.embryo_detail.lab_notes")}
              icon={NotebookText}
            >
              <CardSubtitleText>{embryo.comments}</CardSubtitleText>
            </Section>
          )}
        </>
      )}
    </>
  );
}

/**
 * The page's embryos as chips across the top of the sheet, beside the close
 * button, so the patient can look at another embryo without leaving.
 */
function EmbryoSwitcher({
  choices,
  selectedId,
  onSelect,
}: {
  choices: EmbryoChoice[];
  selectedId: string;
  onSelect: (embryoId: string) => void;
}) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ marginLeft: -24, marginRight: 24, marginTop: -8 }}
      contentContainerStyle={{
        gap: 8,
        paddingLeft: 24,
        paddingRight: 16,
        paddingBottom: 2,
      }}
    >
      {choices.map((choice) => (
        <MoonFilterChip
          key={choice.id}
          label={choice.label}
          selected={choice.id === selectedId}
          onPress={() => onSelect(choice.id)}
          tone="glass"
          showCheck={false}
          action="switch_embryo_in_sheet"
        />
      ))}
    </ScrollView>
  );
}

export default function EmbryoDetailSheet({
  embryo,
  onClose,
  embryoChoices = [],
  onSelectEmbryo,
  ...rest
}: EmbryoDetailSheetProps) {
  const t = useTranslations();
  // The tab outlives the embryo, so switching embryos keeps the same view
  // (for example, chromosomes against chromosomes); a new opening starts on
  // the overview.
  const [tab, setTab] = useState<EmbryoTab>("overview");
  const [term, setTerm] = useState<EmbryoTerm | null>(null);
  useEffect(() => {
    if (!embryo) setTab("overview");
  }, [embryo]);
  useEffect(() => {
    setTerm(null);
  }, [embryo?.id]);

  const explanation = (
    <InfoSheet
      visible={embryo !== null && term !== null}
      onClose={() => setTerm(null)}
      title={term ? t(TERM_KEYS[term].title) : ""}
      sections={term ? [{ body: t(TERM_KEYS[term].body) }] : []}
    />
  );

  return (
    <>
      <DetailSheet
        visible={embryo !== null}
        onClose={onClose}
        closeLabel={t("care.embryo_detail.close_embryo_details")}
        testID="embryo-detail-sheet"
      >
        {embryo && onSelectEmbryo && embryoChoices.length > 1 ? (
          <EmbryoSwitcher
            choices={embryoChoices}
            selectedId={embryo.id}
            onSelect={onSelectEmbryo}
          />
        ) : null}
        {/* Keyed by embryo, so a reveal or open section never carries over. */}
        {embryo && (
          <EmbryoDetailContent
            key={embryo.id}
            embryo={embryo}
            embryoChoices={embryoChoices}
            tab={tab}
            onChangeTab={setTab}
            onExplainTerm={setTerm}
            {...rest}
          />
        )}
        {embryo && tab === "genetics" && (
          <>
            <EmbryoPolygenicComparison
              choices={embryoChoices}
              currentEmbryoId={embryo.id}
              onSelectEmbryo={onSelectEmbryo}
            />
          </>
        )}
        {/* Native modals need the detail sheet's presentation context. */}
        {Platform.OS !== "web" && explanation}
      </DetailSheet>
      {/* Sibling portals keep the explanation above the sheet on web. */}
      {Platform.OS === "web" && explanation}
    </>
  );
}
